/** ID-token lifecycle, provider-agnostic — the single home for keeping the
 * `graphmind_id_token` cookie fresh. Supabase is the live provider; the
 * Firebase module injects into the same seam but is reachable nowhere today
 * (see lib/accounts.ts).
 *
 * Auth is 100% cookie-based: the browser sends the cookie on every same-origin
 * request (fetch, SSE, pdf.js byte-ranges, <img>), and the backend 401s once the
 * embedded JWT expires (both providers mint ~1 hour tokens). Nothing on the
 * server refreshes it, so this module does:
 *   - exp-aware cookie writes (the cookie never outlives the JWT);
 *   - a proactive timer + visibility/online listeners that refresh BEFORE expiry;
 *   - `ensureFreshToken()` — a cheap freshness gate the api layer awaits before
 *     every outbound request (single-flight, so a burst of parallel loads
 *     triggers one refresh, not N);
 *   - `forceRefresh()` — the reactive backstop the api client calls on a 401.
 *
 * This module imports NEITHER `api` nor `accounts` (it sits below both, breaking
 * the `accounts → api` cycle) and no provider SDK. `accounts` injects the
 * active provider's token-getter via `setTokenProvider`. */

const ID_COOKIE = "graphmind_id_token";

/** Refresh this many seconds before the JWT's `exp`. Both providers mint ~1h
 * tokens; 5 min of headroom absorbs clock skew and a backgrounded-tab refresh
 * lag. */
const BUFFER_SECONDS = 300;

/** Mints a fresh token rather than returning the cached (possibly stale) one.
 * Supabase injects `refreshSession()` with a cached-session fallback
 * (accounts-supabase.ts); Firebase injects `getIdToken(true)`
 * (accounts-firebase.ts). Resolves to `null` when no user is signed in. */
type TokenProvider = () => Promise<string | null>;

let provider: TokenProvider | null = null;
let resolveProviderReady!: () => void;
/** Settles when `accounts` has injected the provider — lets the reactive
 * `forceRefresh` ride out a cold page load where accounts init is still in
 * flight. Never awaited on the hot path (see `ensureFreshToken`). */
const providerReady = new Promise<void>((r) => {
  resolveProviderReady = r;
});

/** Seconds-since-epoch `exp` of the token currently in the cookie (null = none
 * known, e.g. signed out or pre-init). */
let currentExp: number | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inflight: Promise<boolean> | null = null;
let listenersInstalled = false;

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

/** Read the `exp` (seconds) out of a JWT payload, or null if it can't be parsed.
 * A valid Firebase token always decodes; we never trust this to fail open. */
export function decodeExp(jwt: string): number | null {
  try {
    const payload = jwt.split(".")[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
    const bin = atob(b64 + pad);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const json = JSON.parse(new TextDecoder().decode(bytes)) as { exp?: number };
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

/** Mirror the ID token into the cookie. Crucially, `max-age` is derived from the
 * token's real `exp` (clamped ≥0) so the browser stops sending the cookie the
 * moment the JWT dies, instead of holding a corpse for a fixed 58 minutes — the
 * root cause of the "auth expires every few minutes" bug. Also (re)arms the
 * proactive refresh timer. Passing `null` clears the cookie (sign-out). */
export function writeIdCookie(token: string | null): void {
  if (!token) {
    currentExp = null;
    document.cookie = `${ID_COOKIE}=; path=/; SameSite=Strict; max-age=0`;
    armProactiveRefresh();
    return;
  }
  const exp = decodeExp(token);
  currentExp = exp;
  if (exp != null) {
    const maxAge = Math.max(0, exp - nowSeconds());
    document.cookie = `${ID_COOKIE}=${token}; path=/; SameSite=Strict; max-age=${maxAge}`;
  } else {
    // Undecodable token (shouldn't happen for a real Firebase JWT): fall back to
    // a session cookie so it dies with the tab rather than lingering.
    document.cookie = `${ID_COOKIE}=${token}; path=/; SameSite=Strict`;
  }
  armProactiveRefresh();
  installGlobalListeners();
}

/** Inject the SDK token-getter. Called once by `accounts` after `getAuth`. */
export function setTokenProvider(fn: TokenProvider): void {
  provider = fn;
  resolveProviderReady();
  installGlobalListeners();
}

/** Single-flight forced refresh: many concurrent callers share one provider
 * call (Firebase rate-limits `getIdToken(true)`). Writes the new cookie and
 * reports whether a token was actually obtained. */
function doRefresh(): Promise<boolean> {
  inflight ??= (async () => {
    try {
      const token = provider ? await provider() : null;
      writeIdCookie(token);
      return token != null;
    } catch {
      return false;
    }
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Cheap freshness gate awaited before every outbound request. Returns instantly
 * when the cached token is comfortably valid (the common case) or when no
 * provider is registered (local mode — nothing to refresh); only awaits a
 * network refresh when within `BUFFER_SECONDS` of expiry. */
export async function ensureFreshToken(): Promise<void> {
  if (!provider) return;
  if (currentExp != null && currentExp - nowSeconds() > BUFFER_SECONDS) return;
  await doRefresh();
}

/** Reactive backstop for a 401/403: force a refresh and report whether a token
 * was obtained, so a genuinely signed-out caller converges to the sign-in card
 * (retry allowed to 401 again) instead of looping. */
export async function forceRefresh(): Promise<boolean> {
  if (!provider) {
    // Hosted cold-load: accounts init may still be resolving firebase. A 401
    // only happens in hosted mode, where the provider WILL arrive shortly.
    await Promise.race([providerReady, new Promise<void>((r) => setTimeout(r, 3000))]);
    if (!provider) return false;
  }
  return doRefresh();
}

/** (Re)arm a single timer to refresh ~`BUFFER_SECONDS` before expiry. This is
 * what keeps long-open documents (pdf.js byte-ranges can't be retried mid-load)
 * authenticated, independent of the provider SDK's own internal scheduler,
 * which is throttled while the tab is backgrounded. */
function armProactiveRefresh(): void {
  if (refreshTimer != null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (currentExp == null) return;
  const delayMs = Math.max(0, (currentExp - BUFFER_SECONDS - nowSeconds()) * 1000);
  refreshTimer = setTimeout(() => {
    void doRefresh();
  }, delayMs);
}

function installGlobalListeners(): void {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;
  // A backgrounded tab throttles timers; refresh on the way back in (and on
  // regaining connectivity) so the user never returns to a dead cookie.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void ensureFreshToken();
  });
  window.addEventListener("online", () => {
    void ensureFreshToken();
  });
}
